import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Instagram reel/post embed extension.
 * Inserts an Instagram embed iframe inline in the editor.
 * Storage format: <div data-instagram-embed="true" data-url="..."><iframe ...></iframe></div>
 */
function extractReelId(url) {
  if (!url) return null;
  // Accept formats:
  // https://www.instagram.com/reel/{ID}/...
  // https://www.instagram.com/p/{ID}/...
  // https://www.instagram.com/tv/{ID}/...
  // Also accept just the ID itself.
  const m = url.match(/instagram\.com\/(reel|p|tv|reels)\/([A-Za-z0-9_-]+)/);
  if (m) return { kind: m[1] === "reels" ? "reel" : m[1], id: m[2] };
  if (/^[A-Za-z0-9_-]+$/.test(url.trim())) return { kind: "p", id: url.trim() };
  return null;
}

export function buildInstagramEmbedUrl(url) {
  const parsed = extractReelId(url);
  if (!parsed) return null;
  // Instagram's official embed endpoint
  return `https://www.instagram.com/${parsed.kind}/${parsed.id}/embed/`;
}

export const InstagramEmbed = Node.create({
  name: "instagramEmbed",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      url: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-instagram-embed]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const url = HTMLAttributes.url;
    const embedUrl = buildInstagramEmbedUrl(url) || url;
    return [
      "div",
      mergeAttributes(
        { "data-instagram-embed": "true", "data-url": url, class: "instagram-embed-wrap" },
        {}
      ),
      [
        "iframe",
        {
          src: embedUrl,
          width: "326",
          height: "560",
          frameborder: "0",
          scrolling: "no",
          allowtransparency: "true",
          allow: "encrypted-media",
          loading: "lazy",
        },
      ],
    ];
  },

  addCommands() {
    return {
      setInstagramEmbed:
        (url) =>
        ({ chain }) =>
          chain()
            .insertContent([
              { type: this.name, attrs: { url } },
              { type: "paragraph" },
            ])
            .focus()
            .run(),
    };
  },
});

export default InstagramEmbed;
